import { useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { EmptyRecord, MainCard, OverflowScroll, StyledTable, StyledRow, ButtonCellCSS, ExpendedCSS } from 'features/incidents/components/style'
import { RowProps } from 'features/incidents/components/type'
import { Box } from 'components/elements'
import { LinearProgress } from '@mui/material'
import { StyledCell } from 'features/incidents/components/SentinelOne/IncidentsTable'
import { IconButton } from 'components/button'
import { DownArrowIcon, UpArrowIcon, RightArrowIcon } from 'components/icons'
import InventoryDetails from './InventoryDetails'

type ColumnConfig = {
  id: string
  label: string
  className: string
  field: string // The field name in the API response
  isDate?: boolean
}

type ArrayFieldConfig = {
  fieldName: string
  titlePrefix: string
}

type GenericInventoryProps = {
  columns: ColumnConfig[]
  data: Record<string, any>[]
  isLoading: boolean
  serviceType?: string
  arrayFields?: ArrayFieldConfig[]
}

const Row = ({
  row,
  open,
  onExpand,
  isDesktopOrLaptop,
  isMobileOrTablet,
  columns,
  arrayFields,
}: RowProps & {
  columns: ColumnConfig[]
  arrayFields?: ArrayFieldConfig[]
}) => {
  return (
    <>
      <StyledRow className={`${open ? 'expanded--bg-color' : undefined}`}>
        {isDesktopOrLaptop && (
          <StyledCell css={{ p: 0, pl: '10px' }}>
            <IconButton onClick={onExpand}>{open ? <UpArrowIcon /> : <DownArrowIcon />}</IconButton>
          </StyledCell>
        )}

        {columns.map(column => {
          if (column.field === 'expand') return null
          return (
            <StyledCell key={column.id}>
              {!isDesktopOrLaptop && (
                <Box as="h4" css={{ mb: 0 }}>
                  {column.label}
                </Box>
              )}
              {column.isDate ? new Date(row?.[column.field]).toDateString() : row?.[column.field] || '_'}
            </StyledCell>
          )
        })}

        {!isDesktopOrLaptop && (
          <StyledCell css={{ p: 0 }}>
            <IconButton css={ButtonCellCSS} className="detail--view" onClick={onExpand}>
              <Box as="h4" css={{ m: '0 1rem 0 0', color: '$primary' }}>
                View Details
              </Box>
              <RightArrowIcon />
            </IconButton>
          </StyledCell>
        )}
      </StyledRow>

      <StyledRow className={open ? 'expanded' : undefined} css={ExpendedCSS}>
        {open && (
          <StyledCell colSpan={20} css={{ p: 0 }}>
            <InventoryDetails row={row} onExpand={onExpand} isDesktopOrLaptop={isDesktopOrLaptop} isMobileOrTablet={isMobileOrTablet} arrayFields={arrayFields} />
          </StyledCell>
        )}
      </StyledRow>
    </>
  )
}

const GenericInventory = ({ columns, data, isLoading, arrayFields = [] }: GenericInventoryProps) => {
  const [openId, setOpenId] = useState(-1)
  const doc: Document = document
  const body = doc.querySelector('body')
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 991 })
  const isMobileOrTablet = useMediaQuery({ minWidth: 767 })

  return (
    <MainCard>
      <Box css={OverflowScroll}>
        <StyledTable css={{ marginBottom: '1rem' }} className={`${!isDesktopOrLaptop && data.length ? 'table--mobile' : 'responsive-mobile'}`}>
          {isDesktopOrLaptop && (
            <thead>
              <StyledRow>
                {columns.map(column => (
                  <StyledCell key={column.id} as="th" className={column.className}>
                    {column.label}
                  </StyledCell>
                ))}
              </StyledRow>
            </thead>
          )}
          <tbody>
            {isLoading ? (
              <th colSpan={columns.length + 1}>
                <LinearProgress sx={{ height: '6px' }} />
              </th>
            ) : (
              <>
                {data.length ? (
                  data.map((row: Record<string, any>, idx: number) => (
                    <Row
                      key={idx}
                      row={row}
                      open={openId === idx}
                      id={idx}
                      onExpand={() => {
                        setOpenId(prev => (prev === idx ? -1 : idx))
                        openId === -1 ? body?.classList.add('overflow--hidden') : body?.classList.remove('overflow--hidden')
                      }}
                      isDesktopOrLaptop={isDesktopOrLaptop}
                      isMobileOrTablet={isMobileOrTablet}
                      columns={columns}
                      arrayFields={arrayFields}
                    />
                  ))
                ) : (
                  <EmptyRecord colSpan={columns.length + 1}>No Data</EmptyRecord>
                )}
              </>
            )}
          </tbody>
        </StyledTable>
      </Box>
    </MainCard>
  )
}

export default GenericInventory
